import { z } from 'zod';
import { CONTACT_CATEGORIES } from './contacts.js';
import { NOTE_TYPES } from './engagement.js';
/**
 * Data Import (#31) — bring client demographics + historical (legacy) notes in
 * from a prior system. The client parses the export file (CSV etc.) and maps its
 * columns to this NORMALIZED shape, so the API contract is stable regardless of
 * which system the data came from; a system-specific column map lives in the web
 * importer, not here.
 *
 * Payloads are JSON and capped at 6 MB (app.ts), and there is no multipart upload,
 * so the client sends contacts in chunks (<= IMPORT_MAX_CONTACTS per request) and
 * accumulates the per-chunk results.
 */
/** Max contacts per import request. The client chunks larger files. */
export const IMPORT_MAX_CONTACTS = 200;
/** One historical note attached to an imported contact. */
export const importNoteSchema = z.object({
    body: z.string().min(1).max(20000),
    type: z.enum(NOTE_TYPES).default('note'),
    /** Original author's name in the source system (free text — not a real user here). */
    authorLabel: z.string().max(200).optional(),
    /** Original note date in the source system; falls back to now() when absent. */
    date: z.coerce.date().optional(),
});
/**
 * #31 — one upcoming appointment for an imported contact, created in the Calendar
 * module (via its API, like the drawer's scheduler). The client resolves the file's
 * date + time in the user's local time zone and only sends upcoming ones; past visits
 * are skipped by design (they'd otherwise surface in RCM's billable Appointments).
 */
export const importAppointmentSchema = z
    .object({
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    /** Appointment type — used as the event title, as the drawer's scheduler does. */
    type: z.string().max(120).optional(),
    /** Clinician: an EHR user's full name or email; unassigned when not found. */
    provider: z.string().max(200).optional(),
    location: z.string().max(300).optional(),
    telehealth: z.boolean().optional(),
})
    .refine((a) => a.endsAt > a.startsAt, { message: 'Appointment must end after it starts' });
/**
 * One imported contact. Plain columns (firstName/lastName/email/phone/category)
 * populate the Contact row; the demographic fields (dob/sex/mrn/address…) are
 * stored in the encrypted demographics blob. `externalId` is the source-system id
 * used to make a re-import idempotent (update the existing contact, not duplicate).
 */
export const importContactSchema = z.object({
    externalId: z.string().max(200).optional(),
    category: z.enum(CONTACT_CATEGORIES).default('client'),
    firstName: z.string().min(1).max(120),
    lastName: z.string().min(1).max(120),
    // Lenient by design: a malformed email in an import file must not 400 the whole
    // batch. Keep it only when it looks like an address, otherwise drop it (the
    // contact is still imported without an email). The strict check lives on the
    // interactive create/demographics endpoints, not here.
    email: z
        .string()
        .max(200)
        .optional()
        .transform((v) => (v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : undefined)),
    phone: z.string().max(40).optional(),
    // Demographics (encrypted blob): identity + contact address sections.
    dob: z.string().max(40).optional(),
    sex: z.string().max(20).optional(),
    mrn: z.string().max(80).optional(),
    address1: z.string().max(200).optional(),
    address2: z.string().max(200).optional(),
    city: z.string().max(120).optional(),
    state: z.string().max(40).optional(),
    zip: z.string().max(20).optional(),
    // #31 follow-up: Status (matched by label in the contact's status pool), Referral
    // source (matched by name, created when missing) and the primary Insurance / Payor
    // (demographics `insurance` section — the one RCM reads for claims).
    status: z.string().max(120).optional(),
    referralSource: z.string().max(200).optional(),
    insurancePayer: z.string().max(200).optional(),
    insuranceMemberId: z.string().max(80).optional(),
    insuranceGroup: z.string().max(80).optional(),
    insurancePhone: z.string().max(40).optional(),
    notes: z.array(importNoteSchema).max(1000).optional(),
    appointments: z.array(importAppointmentSchema).max(500).optional(),
});
export const importBatchSchema = z.object({
    /** Validate + report what WOULD happen without writing anything. */
    dryRun: z.boolean().default(false),
    contacts: z.array(importContactSchema).min(1).max(IMPORT_MAX_CONTACTS),
});
//# sourceMappingURL=import.js.map